import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useControllerSampleSize } from '../../../hooks/analytics/useControllerReports.ts';
import { RefreshMeta } from './shared.tsx';
import type { ControllerSampleSizeRow } from '../../../request/analytics/controllerReports.ts';

// AICPA-style rule-of-thumb minimum sample for a low-risk population; used only
// to flag rows for visual review, not as an authoritative statistical minimum.
const MIN_RECOMMENDED_SAMPLE = 25;

/** Report #23 — Sample Size Report. Sorted by deviation rate desc by default; flags under-sampled controls. */
export const SampleSizeTable: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerSampleSize();

  const columns = useMemo<TableColumnDef<ControllerSampleSizeRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 26,
      },
      {
        id: 'population',
        columnLabel: 'Population',
        header: () => 'Population',
        cell: ({ row }) => row.original.population ?? '—',
        widthPercent: 12,
      },
      {
        id: 'sample_size',
        columnLabel: 'Sample',
        header: () => 'Sample',
        cell: ({ row }) => {
          const size = row.original.sample_size;
          const flagged = size != null && size < MIN_RECOMMENDED_SAMPLE;
          return (
            <span className={flagged ? 'text-amber-600 font-semibold' : ''} title={flagged ? 'Below recommended minimum sample size' : undefined}>
              {size ?? '—'}
            </span>
          );
        },
        widthPercent: 10,
      },
      {
        id: 'sampling_method',
        columnLabel: 'Method',
        header: () => 'Method',
        cell: ({ row }) => row.original.sampling_method ?? '—',
        widthPercent: 16,
      },
      {
        id: 'deviation_rate',
        columnLabel: 'Deviation Rate',
        header: () => 'Deviation Rate',
        cell: ({ row }) => {
          const rate = row.original.deviation_rate;
          if (rate == null) return '—';
          return <span className={rate > 0.05 ? 'text-red-600 font-semibold' : ''}>{(rate * 100).toFixed(1)}%</span>;
        },
        widthPercent: 14,
      },
    ],
    []
  );

  const sortedData = useMemo(() => {
    const base = data ?? [];
    const filtered = controlIdFilter ? base.filter((r) => controlIdFilter.has(r.control_id)) : base;
    return [...filtered].sort((a, b) => (b.deviation_rate ?? 0) - (a.deviation_rate ?? 0));
  }, [data, controlIdFilter]);

  return (
    <AppDataTable<ControllerSampleSizeRow>
      data={sortedData}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load sample size report.' : null}
      emptyMessage="No sample size data available."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
    />
  );
};

export default SampleSizeTable;
